import { FEHLER, type FehlerCode } from '@dxl/shared';

export class ApiFehler extends Error {
  constructor(
    readonly code: FehlerCode | string,
    readonly status: number,
    readonly felder: Record<string, string> = {},
    hinweis?: string,
    /** Weitere Angaben der Antwort (z. B. positionId beim Retouren-Eingang) */
    readonly zusatz: Record<string, unknown> = {},
  ) {
    super(hinweis ?? FEHLER[code as FehlerCode] ?? FEHLER.serverfehler);
  }
}

/* DXL-Ansicht in einem eigenen Tab: das Token liegt nur in diesem Tab (sessionStorage) und geht als
   Kopfzeile mit; Links, Bilder und Downloads bekommen es per mitAnsicht() als Parameter. */
const ANSICHT_SCHLUESSEL = 'dxl-ansicht';
let ansicht: string | null = (() => {
  // Übergabe aus dem öffnenden Tab per Adresse (#ansicht=…) – sofort aus der Adresszeile entfernen
  const aus = /^#ansicht=([A-Za-z0-9_-]{20,100})$/.exec(window.location.hash)?.[1] ?? null;
  if (aus) window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  try {
    if (aus) sessionStorage.setItem(ANSICHT_SCHLUESSEL, aus);
    return aus ?? sessionStorage.getItem(ANSICHT_SCHLUESSEL);
  } catch {
    return aus;
  }
})();
/** true, wenn dieser Tab die DXL-Ansicht eines Marktplatzes zeigt */
export const ansichtImTab = () => !!ansicht;
export function ansichtImTabBeenden() {
  ansicht = null;
  try {
    sessionStorage.removeItem(ANSICHT_SCHLUESSEL);
  } catch {
    /* ohne Speicher gilt die Ansicht ohnehin nur bis zum Neuladen */
  }
}
/** Adresse für Links, Bilder und Downloads – im Ansicht-Tab mit Token */
export function mitAnsicht(url: string): string {
  if (!ansicht) return url;
  return `${url}${url.includes('?') ? '&' : '?'}ansicht=${encodeURIComponent(ansicht)}`;
}
/** DXL-Ansicht in diesem Tab beginnen (Startseite /ansicht/:id, siehe routes/AnsichtStart.tsx) */
export function ansichtImTabStarten(token: string) {
  ansicht = token;
  try {
    sessionStorage.setItem(ANSICHT_SCHLUESSEL, token);
  } catch {
    /* ohne Speicher gilt die Ansicht bis zum Neuladen */
  }
}

let csrf = '';
export const setzeCsrf = (t: string) => {
  csrf = t;
};

let beiAbmeldung: (() => void) | null = null;
/** Wird aufgerufen, wenn der Server die Sitzung nicht mehr kennt */
export const beiSitzungsende = (fn: () => void) => {
  beiAbmeldung = fn;
};

type Methode = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** body = JSON; datei = Rohdaten mit eigenem Content-Type (z. B. Bilder, Nachweise); kopf = zusätzliche Header */
export async function api<T>(pfad: string, optionen: { methode?: Methode; body?: unknown; datei?: Blob; kopf?: Record<string, string>; signal?: AbortSignal } = {}): Promise<T> {
  const methode = optionen.methode ?? (optionen.body === undefined && !optionen.datei ? 'GET' : 'POST');
  const kopf: Record<string, string> = { ...optionen.kopf, accept: 'application/json' };
  if (methode !== 'GET') {
    kopf['content-type'] = optionen.datei ? optionen.datei.type : 'application/json';
    // Das Händlerportal schickt sein eigenes Token mit (lib/portal.ts)
    if (csrf && !kopf['x-csrf-token']) kopf['x-csrf-token'] = csrf;
  }
  if (ansicht && !pfad.startsWith('/api/portal/')) kopf['x-dxl-ansicht'] = ansicht;
  let r: Response;
  try {
    r = await fetch(pfad, {
      method: methode,
      headers: kopf,
      body: methode === 'GET' ? undefined : (optionen.datei ?? JSON.stringify(optionen.body ?? {})),
      credentials: 'same-origin',
      signal: optionen.signal,
    });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiFehler('serverfehler', 0);
  }
  const daten = (await r.json().catch(() => ({}))) as { error?: string; felder?: Record<string, string>; hinweis?: string } | null;
  if (!r.ok || daten === null) {
    if (r.ok) return null as T;
    const f = new ApiFehler(daten?.error ?? 'serverfehler', r.status, daten?.felder, daten?.hinweis, (daten ?? {}) as Record<string, unknown>);
    // KI-Guthaben reicht nicht: Dialog mit Weg zum Aufladen (components/KiGuthaben.tsx)
    if (daten?.error === 'guthaben_fehlt') window.dispatchEvent(new CustomEvent('dxl:ki-guthaben-fehlt'));
    if (r.status === 401 && daten?.error === 'nicht_angemeldet' && !pfad.startsWith('/api/portal/')) {
      // Ansicht im Tab abgelaufen oder Plattform abgemeldet: Tab fällt auf die normale Anmeldung zurück
      ansichtImTabBeenden();
      beiAbmeldung?.();
    }
    throw f;
  }
  return daten as T;
}

/** Überträgt Feldfehler des Servers in react-hook-form */
export function feldfehlerSetzen(
  fehler: unknown,
  setError: (feld: never, f: { message: string }) => void,
): boolean {
  if (!(fehler instanceof ApiFehler) || !Object.keys(fehler.felder).length) return false;
  for (const [feld, text] of Object.entries(fehler.felder)) setError(feld as never, { message: text });
  return true;
}
